import React, { ReactNode } from 'react'

/**
 * Replaces spaces after single-letter words (Polish and English) and common short conjunctions/prepositions
 * with non-breaking spaces (\u00A0) to prevent hanging orphans at the end of lines.
 */
export const preventOrphans = (text: string): string => {
  if (!text || typeof text !== 'string') return text

  const words =
    'and|or|the|to|in|on|of|for|with|by|as|at|an|is|if|oraz|albo|dla|pod|nad|bez|nie|ze|we|od|do|na|że|po|za|co|jak'
  // Note: match regular whitespace [ \t]+ only, preserving already-inserted \u00A0 non-breaking spaces.
  const orphanRegex = new RegExp(
    `(^|[\\s\\[(\"\'‘“„«])([a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]|${words})[ \\t]+`,
    'gi'
  )

  let result = text
  let prev = ''
  while (result !== prev) {
    prev = result
    result = result.replace(orphanRegex, '$1$2\u00A0')
  }

  return result
}

/**
 * Recursively applies orphan prevention to strings within React nodes.
 */
export const formatTextWithOrphans = (node: ReactNode): ReactNode => {
  if (typeof node === 'string') {
    return preventOrphans(node)
  }

  if (Array.isArray(node)) {
    return React.Children.map(node, (child) => formatTextWithOrphans(child))
  }

  if (React.isValidElement(node)) {
    const props = node.props as { children?: ReactNode }
    if (props && props.children) {
      return React.cloneElement(node, {
        ...props,
        children: formatTextWithOrphans(props.children),
      })
    }
  }

  return node
}
